import {
  type AlternativeFuelRatesResponse,
  type MtmYtmRequestPayload,
  type KpiPfyPayload,
  type PlannedAlternativeFuelRatesHierarchy,
  PERFORMANCE_KPI_REQUEST_STALE_TIME
} from '@domain/kpis'
import {ApiClient} from '@hconnect/apiclient'
import {useUpmPlantsContext} from '@hconnect/upm'
import {useQueries, UseQueryResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {isEmpty} from 'lodash'
import {useCallback, useMemo} from 'react'

import {mapAlternativeFuelRatesDto} from '../../mappers'
import {type KpiHookReturn} from '../../types'

import {
  getAlternativeFuelRates,
  getAlternativeFuelRatesMtmYtmQueryKeys
} from './useAlternativeFuelRates'

const getAlternativeFuelRatesPfy = async (payload?: KpiPfyPayload, signal?: AbortSignal) => {
  const url = '/kpi/dashboard/alternative-fuel-rates-pfy'
  const response = await ApiClient.axiosInstance.get<PlannedAlternativeFuelRatesHierarchy>(url, {
    params: {...payload},
    signal
  })

  return response.data
}

export const useAlternativeFuelRatesData = (
  payload: MtmYtmRequestPayload,
  enabled = true
): KpiHookReturn => {
  const upmPlants = useUpmPlantsContext()

  const queries = useQueries<
    [
      UseQueryResult<PlannedAlternativeFuelRatesHierarchy, AxiosError>,
      UseQueryResult<AlternativeFuelRatesResponse, AxiosError>
    ]
  >({
    queries: [
      {
        queryKey: [
          'alternative-fuel-rates-pfy',
          {locationId: payload?.locationId, year: payload?.year}
        ],
        queryFn: ({signal}) =>
          getAlternativeFuelRatesPfy(
            {
              locationId: payload?.locationId,
              year: payload?.year
            },
            signal
          ),
        enabled,
        staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME
      },
      {
        queryKey: getAlternativeFuelRatesMtmYtmQueryKeys(payload),
        queryFn: ({signal}) => getAlternativeFuelRates(payload, signal),
        enabled,
        staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME
      }
    ]
  })

  const refetch = useCallback(() => {
    queries.forEach(({error, refetch: queryRefetch}) => {
      if (error) {
        void queryRefetch()
      }
    })
  }, [queries])

  const [pfyQuery, dataQuery] = queries
  const pfyData = pfyQuery.data
  const data = dataQuery.data

  const alternativeFuelRates = useMemo(() => {
    return !isEmpty(pfyData) && !isEmpty(data)
      ? mapAlternativeFuelRatesDto(data, upmPlants, pfyData)
      : undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pfyData), JSON.stringify(data)])

  return {
    data: alternativeFuelRates,
    isInitialLoading: pfyQuery.isInitialLoading || dataQuery.isInitialLoading,
    error: pfyQuery.error || dataQuery.error,
    refetch
  }
}
